import {
  AlertTriangle,
  BarChart3,
  Bell,
  Briefcase,
  Building2,
  Calendar,
  Clock,
  Database,
  DollarSign,
  FileText,
  HardHat,
  Heart,
  Home,
  KeyRound,
  ListChecks,
  MapPin,
  MessageSquare,
  Package,
  ScanLine,
  Settings,
  Shield,
  User,
  Users,
  type LucideIcon,
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useAuth } from "@/hooks/useAuth"
import { useEnabledModules } from "@/hooks/useEnabledModules"
import { useVersioning } from "@/hooks/useVersioning"
import {
  canAccess,
  navigationDefinitions,
  type NavigationGroup,
  type NavigationIcon,
} from "@/lib/authorization"

const navigationIcons: Record<NavigationIcon, LucideIcon> = {
  home: Home,
  messages: MessageSquare,
  building: Building2,
  users: Users,
  package: Package,
  alert: AlertTriangle,
  calendar: Calendar,
  bell: Bell,
  reports: FileText,
  money: DollarSign,
  areas: MapPin,
  visitor: User,
  access: ScanLine,
  staff: HardHat,
  clock: Clock,
  timesheet: ListChecks,
  pets: Heart,
  settings: Settings,
  briefcase: Briefcase,
  shield: Shield,
  key: KeyRound,
  database: Database,
  analytics: BarChart3,
}

const groupLabels: Record<NavigationGroup, string> = {
  main: "Menu Principal",
  commercial: "Comercial",
  platform: "Administração da Plataforma",
}

export function AppSidebar() {
  const { profile, signOut } = useAuth()
  const { hasModule } = useEnabledModules()
  const { version } = useVersioning()
  const location = useLocation()
  const { state } = useSidebar()

  const authorizedItems = navigationDefinitions
    .filter((item) => canAccess({ role: profile?.role, hasModule }, item))
    .map((item) => ({ ...item, iconComponent: navigationIcons[item.icon] }))

  const getInitials = (name: string | null, email?: string) => {
    if (name?.trim()) {
      return name.split(" ").map((part) => part[0]).join("").toUpperCase()
    }
    return email?.charAt(0).toUpperCase() || "?"
  }

  const isActive = (url: string) =>
    location.pathname === url || (url !== "/dashboard" && location.pathname.startsWith(`${url}/`))

  const renderGroup = (group: NavigationGroup) => {
    const items = authorizedItems.filter((item) => item.group === group)
    if (items.length === 0) return null

    return (
      <SidebarGroup key={group}>
        {state !== "collapsed" && <SidebarGroupLabel>{groupLabels[group]}</SidebarGroupLabel>}
        <SidebarGroupContent>
          <SidebarMenu>
            {items.map((item) => {
              const Icon = item.iconComponent
              return (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={state === "collapsed" ? item.title : undefined}
                    className={state === "collapsed" ? "h-10 w-10 justify-center px-3" : "justify-start px-4"}
                  >
                    <Link
                      to={item.url}
                      className={state === "collapsed" ? "flex items-center justify-center" : "flex items-center"}
                    >
                      <Icon className={`h-4 w-4 flex-shrink-0 ${state === "collapsed" ? "" : "mr-2"}`} />
                      {state !== "collapsed" && <span>{item.title}</span>}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    )
  }

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <div className={`flex items-center ${state === "collapsed" ? "justify-center px-2" : "gap-2 px-4"} py-2`}>
          <h1 className={`font-bold ${state === "collapsed" ? "text-sm" : "text-xl"}`}>
            {state === "collapsed" ? "iC" : "itaCondo"}
          </h1>
        </div>
        {state !== "collapsed" && <p className="px-4 text-sm text-muted-foreground">Sistemas.</p>}
      </SidebarHeader>

      <SidebarContent>
        {renderGroup("main")}
        {renderGroup("commercial")}
        {renderGroup("platform")}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="h-auto py-2 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 flex-shrink-0 rounded-lg">
                    <AvatarFallback className="rounded-lg">
                      {getInitials(profile?.full_name, profile?.email)}
                    </AvatarFallback>
                  </Avatar>
                  {state !== "collapsed" && (
                    <div className="flex min-w-0 flex-1 flex-col gap-1 text-left">
                      <div className="space-y-0.5">
                        <div className="line-clamp-2 break-words text-sm font-semibold leading-tight">
                          {profile?.full_name || "Usuário"}
                        </div>
                        <div className="line-clamp-1 break-words text-xs leading-tight text-muted-foreground">
                          {profile?.email}
                        </div>
                      </div>
                      <div className="text-xs leading-tight text-muted-foreground/70">v{version}</div>
                    </div>
                  )}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side={state === "collapsed" ? "right" : "bottom"}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuItem asChild>
                  <Link to="/configuracoes">
                    <Settings className="mr-2 h-4 w-4" />
                    Configurações
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()}>Sair</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
